import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SendIcon from "@mui/icons-material/Send";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/client/services/user";
import { DifficultyChip, ExecutionResultPanel, LANGUAGES_LIST, PALETTE, SectionPaper } from "../components/shared";
import { completeLesson, fetchCourseDetail, fetchLessonDetail } from "../hooks/useCourses";
import { getDefaultProgrammingLanguage, resolveProgrammingLanguages } from "../utils/programmingLanguages";
import { buildCourseEnrollmentPayload } from "../utils/enrollment";
import { getBoilerplateKey } from "../utils/judge0Languages";

type Props = { courseId: string; onBack: () => void; initialLessonId?: string; onLessonOpen?: (courseId: string, lessonId: string) => void; onLessonClose?: (courseId: string) => void };

const API_CP = "/api/integration/external-service/coding-platform";

// ─── Lesson-type icons ────────────────────────────────────────────────────────
const LESSON_ICONS: Record<string, React.ReactNode> = {
  text: <TextSnippetOutlinedIcon sx={{ fontSize: 16 }} />,
  video: <PlayArrowIcon sx={{ fontSize: 16 }} />,
  quiz: <QuizOutlinedIcon sx={{ fontSize: 16 }} />,
  coding: <CodeOutlinedIcon sx={{ fontSize: 16 }} />,
  mixed: <CodeOutlinedIcon sx={{ fontSize: 16 }} />,
};
const LESSON_LABEL: Record<string, string> = {
  text: "Reading",
  video: "Video",
  quiz: "Quiz",
  coding: "Coding",
  mixed: "Coding + Quiz",
};

// ─── Editor theme tokens (CodeChef-style dark IDE pane) ───────────────────────
const EDITOR = {
  bg: "#1e1e1e",
  bgHeader: "#252526",
  bgConsole: "#181818",
  border: "#333333",
  text: "#d4d4d4",
  textMuted: "#8a8a8a",
  accent: "#3b82f6",
  submit: "#16a34a",
  submitDark: "#15803d",
};

// ─── Styled primitives for the CodeChef-style course page ────────────────────
const HeroSection = styled(Box)(() => ({
  position: "relative",
  borderRadius: "14px",
  overflow: "hidden",
  background: "linear-gradient(135deg, #0b2545 0%, #13315c 45%, #1d4ed8 100%)",
  color: "#fff",
  padding: "22px 26px 20px",
  marginBottom: 16,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at 90% 10%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 10% 90%, rgba(96,165,250,0.25), transparent 50%)",
    pointerEvents: "none",
  },
}));

const ModuleCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "active",
})<{ active?: boolean }>(({ active }) => ({
  borderRadius: "12px",
  border: active ? "2px solid #1d4ed8" : `1px solid ${PALETTE.border}`,
  boxShadow: active ? "0 6px 18px rgba(29,78,216,0.1)" : "0 1px 3px rgba(15,23,42,0.04)",
  overflow: "hidden",
  backgroundColor: "#fff",
  marginBottom: 10,
  transition: "all 0.18s",
  "&:hover": { boxShadow: "0 4px 14px rgba(15,23,42,0.07)" },
}));

// ════════════════════════════════════════════════════════════════════════════
//  Main component
// ════════════════════════════════════════════════════════════════════════════
export default function CoursePlayerView({ courseId, onBack, initialLessonId, onLessonOpen, onLessonClose }: Props) {
  const { user } = useUser();
  const studentId = user?.id ?? "";

  const [course, setCourse] = useState<any>(null);
  const [loadingCourse, setLoadingCourse] = useState(true);
  const [activeLesson, setActiveLesson] = useState<any>(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"curriculum" | "overview">("curriculum");

  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState<Record<number, string>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);

  // Coding state — one editor/language/execution per problem, keyed by problem id
  const [activeProblemIndex, setActiveProblemIndex] = useState(0);
  const [problemStates, setProblemStates] = useState<Record<string, { code: string; language: any; execution: any; submissions: Array<{ code: string; language: string; result: any; submittedAt: Date }>; _activeTab?: string }>>({});
  const [runningKey, setRunningKey] = useState<string | null>(null);

  const problemKey = (problem: any, idx: number) => String(problem?._id ?? problem?.id ?? idx);

  const setProblemCode = (key: string, code: string) =>
    setProblemStates((prev) => ({ ...prev, [key]: { ...prev[key], code } }));

  const setProblemLanguage = (key: string, problem: any, lang: any) =>
    setProblemStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], language: lang, code: problem.boilerplateCode?.[getBoilerplateKey(lang)] ?? lang.boilerplate },
    }));

  const setProblemTab = (key: string, tab: string) =>
    setProblemStates((prev) => ({ ...prev, [key]: { ...prev[key], _activeTab: tab } }));

  const [completing, setCompleting] = useState(false);
  const [notice, setNotice] = useState<{ type: "success" | "error" | "info" | "warning"; msg: string } | null>(null);

  // ── Load course ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!courseId || !studentId) return;
    setLoadingCourse(true);
    fetchCourseDetail(courseId, studentId).then((data) => {
      setCourse(data);
      const firstModule = data?.modules?.[0];
      if (firstModule) setExpandedModules(new Set([firstModule.id]));
      // If a lessonId was in the URL, auto-open that lesson
      if (initialLessonId) {
        const allLessons = (data?.modules ?? []).flatMap((m: any) => m.lessons ?? []);
        const target = allLessons.find((l: any) => l.id === initialLessonId);
        if (target) {
          // Delay so course state is set before openLesson runs
          setTimeout(() => openLessonById(data, target), 0);
        }
      }
    }).finally(() => setLoadingCourse(false));
  }, [courseId, studentId]);

  const handleCourseUpdate = (updated: any) => {
    if (updated) setCourse(updated);
  };

  // ── Open a lesson (switches into the IDE-style workspace) ─────────────────
  // Core logic — accepts explicit courseData so it can be called before course state is set
  const openLessonById = async (courseData: any, lesson: any) => {
    const isEnrolled = !!(courseData?.enrolled || courseData?.enrollment);
    if (lesson.isLocked || !isEnrolled) return;
    setLoadingLesson(true);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setNotice(null);
    setActiveProblemIndex(0);
    try {
      const detail = await fetchLessonDetail(courseId, lesson.id, studentId);
      setActiveLesson(detail);
      const problems = detail.problemIds ?? [];
      const defaultLang = getDefaultProgrammingLanguage(course?.programmingLanguage || course?.language);

      // Fetch existing submissions for every problem in this lesson in parallel
      const submissionsByKey: Record<string, Array<{ code: string; language: string; result: any; submittedAt: Date }>> = {};
      await Promise.all(
        problems.map(async (p: any, idx: number) => {
          const pid = p._id ?? p.id;
          const key = problemKey(p, idx);
          try {
            const res = await axios.get(`${API_CP}/submissions/${studentId}/by-problem/${pid}`, {
              params: { limit: 50 },
            });
            const rows: any[] = Array.isArray(res?.data?.result) ? res.data.result
              : Array.isArray(res?.data?.data) ? res.data.data
              : Array.isArray(res?.data) ? res.data : [];
            submissionsByKey[key] = rows.map((r: any) => ({
              code: r.code ?? "",
              language: r.programmingLanguage ?? "",
              result: {
                status: r.status,
                passedTestCases: r.passedTestCases,
                totalTestCases: r.totalTestCases,
                error: r.error ?? null,
              },
              submittedAt: r.createdAt ? new Date(r.createdAt) : new Date(),
            }));
          } catch {
            submissionsByKey[key] = [];
          }
        }),
      );

      const initialStates: Record<string, { code: string; language: any; execution: any; submissions: Array<{ code: string; language: string; result: any; submittedAt: Date }>; _activeTab?: string }> = {};
      problems.forEach((p: any, idx: number) => {
        const key = problemKey(p, idx);
        initialStates[key] = {
          code: p.boilerplateCode?.[getBoilerplateKey(defaultLang)] ?? defaultLang.boilerplate,
          language: defaultLang,
          execution: null,
          submissions: submissionsByKey[key] ?? [],
          _activeTab: "statement",   // always start on Statement tab
        };
      });
      setProblemStates(initialStates);
      if (lesson.moduleId) {
        setExpandedModules((prev) => new Set(prev).add(lesson.moduleId));
      }
      // Update URL to reflect the open lesson
      onLessonOpen?.(courseId, lesson.id);
    } finally {
      setLoadingLesson(false);
    }
  };

  // Public openLesson uses the current course state
  const openLesson = (lesson: any) => openLessonById(course, lesson);

  const backToCurriculum = () => {
    setActiveLesson(null);
    setProblemStates({});
    setActiveProblemIndex(0);
    setRunningKey(null);
    setNotice(null);
    // Update URL back to course overview
    onLessonClose?.(courseId);
  };

  const toggleModule = (id: string) =>
    setExpandedModules((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  // ── Quiz submit ─────────────────────────────────────────────────────────────
  const submitQuiz = () => {
    const questions = activeLesson?.quizQuestions ?? [];
    let correct = 0;
    questions.forEach((q: any, i: number) => {
      if (quizAnswers[i] === q.correctAnswer) correct++;
    });
    setQuizScore(correct);
    setQuizSubmitted(true);
  };

  // ── Code run/submit — runs the code for one specific problem ────────────────
  const runCode = async (problemIndex: number, submit: boolean) => {
    const problems = activeLesson?.problemIds ?? [];
    const problem = problems[problemIndex];
    if (!problem) return;
    const key = problemKey(problem, problemIndex);
    const state = problemStates[key];
    if (!state) return;
    setRunningKey(key);
    setProblemStates((prev) => ({ ...prev, [key]: { ...prev[key], execution: null } }));
    try {
      const res = await axios.post(`${API_CP}/${submit ? "submit" : "run"}`, {
        studentId,
        problemId: problem._id ?? problem.id,
        code: state.code,
        programmingLanguage: state.language.key ?? state.language.name,
        programmingLanguageId: state.language.id,
      });
      const data = res?.data?.result ?? res?.data;
      setProblemStates((prev) => {
        const updated = { ...prev[key], execution: data };
        if (submit) {
          updated.submissions = [
            { code: state.code, language: state.language.key ?? state.language.name, result: data, submittedAt: new Date() },
            ...(prev[key]?.submissions ?? []),
          ];
          updated._activeTab = "submissions";
        }
        return { ...prev, [key]: updated };
      });
    } catch (e: any) {
      setNotice({ type: "error", msg: e?.response?.data?.message ?? "Execution failed" });
    } finally {
      setRunningKey(null);
    }
  };

  // ── Complete lesson ─────────────────────────────────────────────────────────
  const markComplete = async () => {
    if (!activeLesson || !studentId) return;
    setCompleting(true);
    const problems = activeLesson.problemIds ?? [];
    const isCodingLesson = activeLesson.type === "coding" || activeLesson.type === "mixed";
    const qs = activeLesson.quizQuestions ?? [];

    // Check which problems are unsolved before calling the API
    const unsolvedProblems = isCodingLesson
      ? problems.filter((_: any, idx: number) => {
          const state = problemStates[problemKey(problems[idx], idx)];
          return !(state?.execution?.status === "accepted" ||
            (state?.submissions ?? []).some((s: any) => s.result?.status === "accepted"));
        })
      : [];

    const codingPassed = !isCodingLesson || (problems.length > 0 && unsolvedProblems.length === 0);

    // Build a clear failure message before hitting the API
    if (!codingPassed) {
      const solvedCount = problems.length - unsolvedProblems.length;
      const remaining = unsolvedProblems.length;
      const noun = remaining === 1 ? "problem" : "problems";
      setNotice({
        type: "warning",
        msg: `Submit and pass ${remaining} more coding ${noun} to complete this lesson. (${solvedCount}/${problems.length} solved)`,
      });
      setCompleting(false);
      return;
    }

    try {
      const result = await completeLesson({
        studentId,
        lessonId: activeLesson.id,
        courseId,
        quizScore,
        quizTotal: qs.length,
        codingPassed,
      });
      setNotice({
        type: result.completed ? "success" : "warning",
        msg: result.completed
          ? `Lesson completed! +${result.xpEarned} XP`
          : qs.length > 0
            ? `Quiz score too low — need at least 60% to complete this lesson. (${quizScore}/${qs.length})`
            : "Could not complete the lesson. Please try again.",
      });
      if (result.completed) {
        const updated = await fetchCourseDetail(courseId, studentId);
        setCourse(updated);
        setActiveLesson((prev: any) => ({ ...prev, progress: { completed: true } }));
        const allLessons = (updated?.modules ?? []).flatMap((m: any) => m.lessons ?? []);
        const idx = allLessons.findIndex((l: any) => l.id === activeLesson.id);
        if (idx >= 0 && idx + 1 < allLessons.length) openLesson(allLessons[idx + 1]);
      }
    } finally {
      setCompleting(false);
    }
  };

  if (loadingCourse) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Alert severity="error">Course not found.</Alert>
      </Box>
    );
  }

  // When a lesson is active, render the CodeChef-style IDE workspace
  if (activeLesson) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <LessonWorkspace
        course={course}
        activeLesson={activeLesson}
        loadingLesson={loadingLesson}
        openLesson={openLesson}
        backToCurriculum={backToCurriculum}
        activeProblemIndex={activeProblemIndex}
        setActiveProblemIndex={setActiveProblemIndex}
        problemStates={problemStates}
        setProblemCode={setProblemCode}
        setProblemLanguage={setProblemLanguage}
        setProblemTab={setProblemTab}
        problemKey={problemKey}
        runningKey={runningKey}
        runCode={runCode}
        completing={completing}
        markComplete={markComplete}
        notice={notice}
        setNotice={setNotice}
        quizAnswers={quizAnswers}
        setQuizAnswers={setQuizAnswers}
        quizSubmitted={quizSubmitted}
        setQuizSubmitted={setQuizSubmitted}
        quizScore={quizScore}
        submitQuiz={submitQuiz}
      />
      </Box>
    );
  }

  // Otherwise, render the CodeChef-style curriculum overview
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 2 }}>
      <CurriculumOverview
      course={course}
      expandedModules={expandedModules}
      toggleModule={toggleModule}
      openLesson={openLesson}
      onBack={onBack}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      studentId={studentId}
      onCourseUpdate={handleCourseUpdate}
    />
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Curriculum Overview (CodeChef-style course landing page) — unchanged
// ════════════════════════════════════════════════════════════════════════════
function CurriculumOverview({
  course,
  expandedModules,
  toggleModule,
  openLesson,
  onBack,
  activeTab,
  setActiveTab,
  studentId,
  onCourseUpdate,
}: {
  course: any;
  expandedModules: Set<string>;
  toggleModule: (id: string) => void;
  openLesson: (lesson: any) => void;
  onBack: () => void;
  activeTab: "curriculum" | "overview";
  setActiveTab: (t: "curriculum" | "overview") => void;
  studentId: string;
  onCourseUpdate: (updated: any) => void;
}) {
  const { user } = useUser();
  const allLessons = (course.modules ?? []).flatMap((m: any) => m.lessons ?? []);
  const completedIds: string[] = course.enrollment?.completedLessonIds ?? [];
  const completedCount = completedIds.length;
  const progressPercent = course.enrollment?.progressPercent ?? 0;
  const isEnrolled = !!(course.enrolled || course.enrollment);
  const courseLanguage = course.language || course.programmingLanguage;

  const totalProblems = useMemo(
    () =>
      (course.modules ?? []).reduce(
        (acc: number, m: any) =>
          acc +
          (m.lessons ?? []).reduce(
            (a: number, l: any) => a + (Array.isArray(l.problemIds) ? l.problemIds.length : 0),
            0,
          ),
        0,
      ),
    [course],
  );

  const learnerCount = course.enrollmentCount ?? 0;
  const rating = course.rating ?? 0;

  const enroll = async () => {
    await axios.post(
      `${API_CP}/courses/enroll`,
      buildCourseEnrollmentPayload(user, course.id),
    );
    const fresh = await fetchCourseDetail(course.id, studentId);
    if (fresh) onCourseUpdate(fresh);
  };

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Button startIcon={<ArrowBackIcon fontSize="small" />} onClick={onBack} size="small" sx={{ textTransform: "none", color: PALETTE.textSecondary, fontSize: 13 }}>
          Back to Courses
        </Button>
      </Stack>

      <HeroSection>
        <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={2} sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={course.difficulty}
                sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, textTransform: "capitalize", fontSize: "10px", height: 22 }}
              />
              {courseLanguage && (
                <Chip
                  size="small"
                  icon={<LanguageIcon sx={{ fontSize: 13, color: "#fff !important" }} />}
                  label={courseLanguage}
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "#fff", fontSize: "10px", height: 22, textTransform: "capitalize" }}
                />
              )}
              {course.hasCertificate && (
                <Chip
                  size="small"
                  icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 13, color: "#fde68a !important" }} />}
                  label="Certificate"
                  sx={{ bgcolor: "rgba(253,230,138,0.18)", color: "#fde68a", fontSize: "10px", height: 22, fontWeight: 700 }}
                />
              )}
              {course.isFeatured && (
                <Chip size="small" label="★ Featured" sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontSize: "10px", height: 22, fontWeight: 700 }} />
              )}
            </Stack>

            <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, lineHeight: 1.15, mb: 1 }}>{course.title}</Typography>
            <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.85)", lineHeight: 1.5, maxWidth: 640, mb: 1.25 }}>{course.shortDescription}</Typography>

            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {rating > 0 && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <StarRoundedIcon sx={{ fontSize: 16, color: "#fde68a" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>{rating.toFixed(1)}</Typography>
                  <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.7)" }}>rating</Typography>
                </Stack>
              )}
              {learnerCount > 0 && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <SchoolOutlinedIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)" }} />
                  <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
                    {learnerCount >= 1000 ? `${(learnerCount / 1000).toFixed(1)}k` : learnerCount}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.7)" }}>learners</Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={0.5} alignItems="center">
                <AccessTimeIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)" }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>{course.estimatedHours ?? 0}h</Typography>
                <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.7)" }}>total</Typography>
              </Stack>
            </Stack>
          </Box>

          <Paper
            sx={{
              p: 2,
              borderRadius: "12px",
              minWidth: 200,
              maxWidth: 220,
              width: "100%",
              bgcolor: "rgba(255,255,255,0.96)",
              color: PALETTE.textSecondary,
              boxShadow: "0 12px 28px rgba(0,0,0,0.14)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontSize: "10px", color: PALETTE.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Your progress
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#1d4ed8" }}>{progressPercent}%</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ height: 5, borderRadius: 3, mb: 1, bgcolor: "#e0e7ff", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg,#1d4ed8,#3b82f6)" } }}
            />
            <Typography sx={{ fontSize: "11px", color: PALETTE.textSecondary, mb: 1.5 }}>
              {completedCount}/{allLessons.length} lessons completed
            </Typography>
            {isEnrolled ? (
              <Button
                fullWidth
                variant="contained"
                size="medium"
                startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                onClick={() => {
                  const next = allLessons.find((l: any) => !completedIds.includes(l.id)) ?? allLessons[0];
                  if (next) openLesson(next);
                }}
                sx={{ textTransform: "none", fontWeight: 700, py: 0.85, fontSize: 13, background: "linear-gradient(90deg,#1d4ed8,#3b82f6)", boxShadow: "0 4px 14px rgba(29,78,216,0.35)" }}
              >
                {progressPercent > 0 ? "Continue Learning" : "Start Learning"}
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                size="medium"
                onClick={enroll}
                sx={{ textTransform: "none", fontWeight: 700, py: 0.85, fontSize: 13, background: "linear-gradient(90deg,#1d4ed8,#3b82f6)", boxShadow: "0 4px 14px rgba(29,78,216,0.35)" }}
              >
                Enroll Free
              </Button>
            )}
          </Paper>
        </Stack>
      </HeroSection>

      <Paper sx={{ display: "flex", alignItems: "center", borderRadius: "10px", border: `1px solid ${PALETTE.border}`, mb: 1.5, overflow: "hidden" }}>
        {[
          { id: "curriculum", label: `Modules (${course.totalModules ?? 0})` },
          { id: "overview", label: "Overview" },
        ].map((t) => (
          <Box
            key={t.id}
            onClick={() => setActiveTab(t.id as any)}
            sx={{
              px: 2,
              py: 1.1,
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 700,
              color: activeTab === t.id ? "#1d4ed8" : PALETTE.textSecondary,
              borderBottom: activeTab === t.id ? "2px solid #1d4ed8" : "2px solid transparent",
              transition: "all 0.15s",
              "&:hover": { color: activeTab === t.id ? "#1d4ed8" : PALETTE.textMuted },
            }}
          >
            {t.label}
          </Box>
        ))}
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 260px" }, gap: 2, alignItems: "start" }}>
        <Box>
          {activeTab === "curriculum" ? (
            <Box>
              {!isEnrolled && (
                <Alert severity="info" sx={{ mb: 1.5, fontSize: 13 }}>
                  Enroll in this course to unlock lessons and start learning.
                </Alert>
              )}
              <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0f172a", mb: 1 }}>Course Curriculum</Typography>
              <Stack spacing={0}>
                {(course.modules ?? []).map((mod: any, mi: number) => {
                  const isOpen = expandedModules.has(mod.id);
                  const moduleCompleted = (mod.lessons ?? []).every((l: any) => completedIds.includes(l.id));
                  return (
                    <ModuleCard key={mod.id} active={isOpen}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        onClick={() => toggleModule(mod.id)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          cursor: "pointer",
                          borderBottom: isOpen ? `1px solid ${PALETTE.border}` : "none",
                          bgcolor: isOpen ? "#f8fafc" : "transparent",
                          "&:hover": { bgcolor: "#f8fafc" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            bgcolor: moduleCompleted ? "#dcfce7" : "#eff6ff",
                            color: moduleCompleted ? "#16a34a" : "#1d4ed8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: 800,
                            flexShrink: 0,
                          }}
                        >
                          {moduleCompleted ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : String(mi + 1).padStart(2, "0")}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0f172a", mb: 0.15 }}>{mod.title}</Typography>
                          <Typography sx={{ fontSize: "11px", color: PALETTE.textSecondary, lineHeight: 1.35 }}>
                            {mod.description || `${(mod.lessons ?? []).length} lessons`}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={`${(mod.lessons ?? []).length} ${(mod.lessons ?? []).length === 1 ? "lesson" : "lessons"}`}
                          sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: "10px", height: 22, mr: 0.5 }}
                        />
                        <ChevronRightIcon sx={{ fontSize: 18, color: PALETTE.textMuted, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
                      </Stack>
                      <Collapse in={isOpen}>
                        <Box>
                          {(mod.lessons ?? []).map((lesson: any) => {
                            const isDone = completedIds.includes(lesson.id);
                            const lessonProblemCount = Array.isArray(lesson.problemIds) ? lesson.problemIds.length : 0;
                            return (
                              <Stack
                                key={lesson.id}
                                direction="row"
                                alignItems="center"
                                spacing={1.25}
                                onClick={() => isEnrolled && !lesson.isLocked && openLesson(lesson)}
                                sx={{
                                  px: 2,
                                  py: 1.1,
                                  cursor: !isEnrolled || lesson.isLocked ? "not-allowed" : "pointer",
                                  borderTop: `1px solid ${PALETTE.border}`,
                                  bgcolor: "transparent",
                                  opacity: !isEnrolled || lesson.isLocked ? 0.6 : 1,
                                  transition: "background 0.12s",
                                  "&:hover": { bgcolor: !isEnrolled || lesson.isLocked ? "transparent" : "#f0f6ff" },
                                }}
                              >
                                <Box sx={{ color: isDone ? "#16a34a" : PALETTE.textMuted, display: "flex", flexShrink: 0 }}>
                                  {isDone ? <CheckCircleIcon sx={{ fontSize: 17 }} /> : lesson.isLocked ? <LockOutlinedIcon sx={{ fontSize: 17 }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 17 }} />}
                                </Box>
                                <Box sx={{ color: "#1d4ed8", display: "flex", flexShrink: 0 }}>{LESSON_ICONS[lesson.type] ?? LESSON_ICONS.text}</Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: isDone ? 600 : 500,
                                      textDecoration: isDone ? "line-through" : "none",
                                      color: isDone ? PALETTE.textSecondary : "#0f172a",
                                    }}
                                    noWrap
                                  >
                                    {lesson.title}
                                  </Typography>
                                  <Typography sx={{ fontSize: "10px", color: PALETTE.textMuted, mt: 0.15 }}>
                                    {LESSON_LABEL[lesson.type] ?? "Reading"}
                                    {lessonProblemCount > 0 && ` · ${lessonProblemCount} ${lessonProblemCount === 1 ? "problem" : "problems"}`}
                                    {lesson.estimatedMinutes > 0 && ` · ${lesson.estimatedMinutes} min`}
                                  </Typography>
                                </Box>
                                <Button
                                  size="small"
                                  variant={isDone ? "outlined" : "contained"}
                                  disabled={lesson.isLocked || !isEnrolled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isEnrolled && !lesson.isLocked) openLesson(lesson);
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    height: 26,
                                    minWidth: 68,
                                    px: 1.25,
                                    ...(isDone
                                      ? { borderColor: PALETTE.borderDark, color: PALETTE.textSecondary }
                                      : !isEnrolled
                                        ? { bgcolor: "#e5e7eb", color: PALETTE.textMuted, boxShadow: "none" }
                                        : { background: "linear-gradient(90deg,#1d4ed8,#3b82f6)", boxShadow: "0 2px 6px rgba(29,78,216,0.25)" }),
                                  }}
                                >
                                  {!isEnrolled ? "Locked" : isDone ? "Review" : "Start"}
                                </Button>
                              </Stack>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </ModuleCard>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <OverviewTab course={course} totalProblems={totalProblems} />
          )}
        </Box>

        <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>
          <CourseInfoSidebar course={course} totalProblems={totalProblems} />
        </Box>
      </Box>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Overview tab — unchanged
// ════════════════════════════════════════════════════════════════════════════
function OverviewTab({ course, totalProblems }: { course: any; totalProblems: number }) {
  return (
    <Stack spacing={1.5}>
      <SectionPaper>
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0f172a", mb: 1 }}>About this course</Typography>
          <Typography sx={{ fontSize: "12.5px", color: "#334155", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
            {course.description || "No description available."}
          </Typography>
          {course.shortDescription && course.shortDescription !== course.description && (
            <Typography sx={{ fontSize: "13px", color: PALETTE.textSecondary, mt: 1.5, fontStyle: "italic" }}>{course.shortDescription}</Typography>
          )}
        </Box>
      </SectionPaper>

      {Array.isArray(course.tags) && course.tags.length > 0 && (
        <SectionPaper>
          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a", mb: 1.5 }}>Tags</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              {course.tags.map((t: string) => (
                <Chip key={t} label={t} size="small" sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontSize: "12px", fontWeight: 600 }} />
              ))}
            </Stack>
          </Box>
        </SectionPaper>
      )}

      {Array.isArray(course.prerequisites) && course.prerequisites.length > 0 && (
        <SectionPaper>
          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a", mb: 1.5 }}>Prerequisites</Typography>
            <Stack spacing={1}>
              {course.prerequisites.map((p: string, i: number) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <VerifiedIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                  <Typography sx={{ fontSize: "13.5px", color: "#334155" }}>{p}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </SectionPaper>
      )}

      {totalProblems > 0 && (
        <SectionPaper>
          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a", mb: 1 }}>What you'll practice</Typography>
            <Typography sx={{ fontSize: "13px", color: PALETTE.textSecondary }}>
              {totalProblems} coding {totalProblems === 1 ? "problem" : "problems"} spread across {course.totalModules ?? 0} modules — solved right in the
              browser with our built-in editor.
            </Typography>
          </Box>
        </SectionPaper>
      )}
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Right sidebar — unchanged
// ════════════════════════════════════════════════════════════════════════════
function CourseInfoSidebar({ course, totalProblems }: { course: any; totalProblems: number }) {
  const courseLanguage = course.language || course.programmingLanguage;
  return (
    <SectionPaper>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0f172a", mb: 1.5 }}>Course Info</Typography>
        <Stack spacing={1.25}>
          <InfoRow icon={<LanguageIcon sx={{ fontSize: 15 }} />} label="Language" value={courseLanguage || "—"} />
          <InfoRow icon={<SchoolOutlinedIcon sx={{ fontSize: 15 }} />} label="Level" value={(course.difficulty || "—") + ""} capitalize />
          <InfoRow icon={<AccessTimeIcon sx={{ fontSize: 15 }} />} label="Duration" value={`${course.estimatedHours ?? 0} hours`} />
          <InfoRow icon={<OndemandVideoIcon sx={{ fontSize: 15 }} />} label="Modules" value={`${course.totalModules ?? 0} modules · ${course.totalLessons ?? 0} lessons`} />
          <InfoRow
            icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Certificate"
            value={course.hasCertificate ? "Yes (on completion)" : "No"}
            valueColor={course.hasCertificate ? "#16a34a" : undefined}
          />
          {totalProblems > 0 && <InfoRow icon={<CodeOutlinedIcon sx={{ fontSize: 15 }} />} label="Coding problems" value={`${totalProblems}`} />}
        </Stack>

        {course.instructor && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0f172a", mb: 1.25 }}>Instructor</Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar sx={{ bgcolor: "#1d4ed8", width: 34, height: 34, fontSize: "13px", fontWeight: 800 }}>
                {course.instructor?.[0]?.toUpperCase() ?? "I"}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#0f172a" }} noWrap>
                  {course.instructor}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: PALETTE.textMuted }}>Course instructor</Typography>
              </Box>
            </Stack>
          </>
        )}

        {Array.isArray(course.tags) && course.tags.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#0f172a", mb: 0.75 }}>What you'll learn</Typography>
            <Stack spacing={1}>
              {course.tags.slice(0, 6).map((t: string, i: number) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                  <VerifiedIcon sx={{ fontSize: 16, color: "#16a34a", mt: 0.25 }} />
                  <Typography sx={{ fontSize: "12.5px", color: "#334155", lineHeight: 1.4 }}>{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </SectionPaper>
  );
}

function InfoRow({
  icon,
  label,
  value,
  capitalize,
  valueColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  capitalize?: boolean;
  valueColor?: string;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ color: "#1d4ed8", display: "flex" }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "11px", color: PALETTE.textMuted, textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: valueColor ?? "#0f172a", textTransform: capitalize ? "capitalize" : "none" }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Lesson Workspace — CodeChef-style split-pane IDE
//  (problem statement left · code editor + console right, top problem switcher)
// ════════════════════════════════════════════════════════════════════════════
function LessonWorkspace({
  course,
  activeLesson,
  loadingLesson,
  openLesson,
  backToCurriculum,
  activeProblemIndex,
  setActiveProblemIndex,
  problemStates,
  setProblemCode,
  setProblemLanguage,
  setProblemTab,
  problemKey,
  runningKey,
  runCode,
  completing,
  markComplete,
  notice,
  setNotice,
  quizAnswers,
  setQuizAnswers,
  quizSubmitted,
  setQuizSubmitted,
  quizScore,
  submitQuiz,
}: any) {
  const isCoding = activeLesson?.type === "coding" || activeLesson?.type === "mixed";
  const allLessons = (course.modules ?? []).flatMap((m: any) => m.lessons ?? []);
  const completedIds: string[] = course.enrollment?.completedLessonIds ?? [];
  const problems = activeLesson?.problemIds ?? [];
  const activeProblem = problems[activeProblemIndex];
  const activeKey = activeProblem ? problemKey(activeProblem, activeProblemIndex) : null;
  const activeState = activeKey ? problemStates[activeKey] : null;
  const courseLanguage = course.language || course.programmingLanguage;
  const allowedLanguages = useMemo(() => resolveProgrammingLanguages(courseLanguage), [courseLanguage]);
  const lessonIndex = allLessons.findIndex((l: any) => l.id === activeLesson?.id);
  const nextLesson = lessonIndex >= 0 && lessonIndex < allLessons.length - 1 ? allLessons[lessonIndex + 1] : null;
  const prevLesson = lessonIndex > 0 ? allLessons[lessonIndex - 1] : null;
  const hasNextProblem = activeProblemIndex < problems.length - 1;
  const hasPrevProblem = activeProblemIndex > 0;

  const goNextProblem = () => setActiveProblemIndex((i: number) => Math.min(i + 1, problems.length - 1));
  const goNextLesson = () => { if (nextLesson && !nextLesson.isLocked) openLesson(nextLesson); };
  const goPrevProblem = () => setActiveProblemIndex((i: number) => Math.max(i - 1, 0));
  const goPrevLesson = () => { if (prevLesson && !prevLesson.isLocked) openLesson(prevLesson); };

  // A problem is "solved" if the current execution OR any past submission was accepted
  const isSolved = (state: any) =>
    state?.execution?.status === "accepted" ||
    (state?.submissions ?? []).some((s: any) => s.result?.status === "accepted");

  // (tab switching on submit is handled directly inside runCode in CoursePlayerView)

  // Problem switcher dropdown (replaces a persistent sidebar, like the "Problems" menu on CodeChef)
  const [switcherAnchor, setSwitcherAnchor] = useState<null | HTMLElement>(null);

  // Draggable split between the statement pane and the editor pane
  const [splitPct, setSplitPct] = useState(42);
  const dragging = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.min(70, Math.max(28, pct)));
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // When the user navigates to a different lesson (Next Lesson button, Problems
  // dropdown, or auto-advance after Mark Complete), reset scroll position so the
  // new lesson starts from the top instead of wherever the previous one ended.
  // We only run on lesson-id change so we don't fight the user mid-lesson.
  useEffect(() => {
    const root = workspaceRef.current;
    if (!root) return;
    // Reset any page-level scroll too (defensive — outer container is overflow:hidden).
    if (typeof window !== "undefined") window.scrollTo(0, 0);
    const all = root.querySelectorAll<HTMLElement>("*");
    all.forEach((el) => {
      // Don't clobber the user's scroll position inside the code editor.
      if (el.tagName === "TEXTAREA") return;
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
        el.scrollTop = 0;
      }
      if ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth) {
        el.scrollLeft = 0;
      }
    });
  }, [activeLesson?.id]);

  return (
    <Box ref={workspaceRef} sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%", overflow: "hidden" }}>
      {/* ── Top bar: breadcrumb + problem switcher + progress ── */}
      <Paper
        sx={{
          px: 2,
          py: 1,
          mb: 1,
          borderRadius: "10px",
          border: `1px solid ${PALETTE.border}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button startIcon={<ArrowBackIcon fontSize="small" />} onClick={backToCurriculum} size="small" sx={{ textTransform: "none", color: PALETTE.textSecondary }}>
          Exit
        </Button>
        <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "11px", color: PALETTE.textMuted }} noWrap>
            {course.title}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "14px", color: "#0f172a" }} noWrap>
            {activeLesson?.title}
          </Typography>
        </Box>

        <Button
          size="small"
          onClick={(e) => setSwitcherAnchor(e.currentTarget)}
          endIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{ textTransform: "none", fontSize: "12px", fontWeight: 700, color: "#1d4ed8", bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" } }}
        >
          Problems
        </Button>
        <Menu anchorEl={switcherAnchor} open={!!switcherAnchor} onClose={() => setSwitcherAnchor(null)} PaperProps={{ sx: { maxHeight: 420, width: 320 } }}>
          {(course.modules ?? []).map((mod: any) => [
            <MenuItem key={`h-${mod.id}`} disabled sx={{ opacity: "1 !important", fontSize: "11px", fontWeight: 800, color: PALETTE.textMuted, textTransform: "uppercase" }}>
              {mod.title}
            </MenuItem>,
            ...(mod.lessons ?? []).map((lesson: any) => {
              const isDone = completedIds.includes(lesson.id);
              const isActive = lesson.id === activeLesson?.id;
              return (
                <MenuItem
                  key={lesson.id}
                  disabled={lesson.isLocked}
                  selected={isActive}
                  onClick={() => {
                    setSwitcherAnchor(null);
                    openLesson(lesson);
                  }}
                  sx={{ fontSize: "13px", gap: 1 }}
                >
                  <Box sx={{ color: isDone ? "#16a34a" : PALETTE.textMuted, display: "flex", flexShrink: 0 }}>
                    {isDone ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : lesson.isLocked ? <LockOutlinedIcon sx={{ fontSize: 16 }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />}
                  </Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: isActive ? 700 : 500 }} noWrap>
                    {lesson.title}
                  </Typography>
                </MenuItem>
              );
            }),
          ])}
        </Menu>

        <Box sx={{ minWidth: 130, display: { xs: "none", sm: "block" } }}>
          <LinearProgress variant="determinate" value={course.enrollment?.progressPercent ?? 0} sx={{ height: 6, borderRadius: 3, mb: 0.25 }} />
          <Typography sx={{ fontSize: "10px", color: PALETTE.textMuted, textAlign: "right" }}>
            {(completedIds.length)}/{allLessons.length} lessons · {course.enrollment?.progressPercent ?? 0}%
          </Typography>
        </Box>
      </Paper>

      {notice && (
        <Alert severity={notice.type} onClose={() => setNotice(null)} sx={{ mb: 1, fontSize: "13px", flexShrink: 0 }}>
          {notice.msg}
        </Alert>
      )}

      {loadingLesson ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : isCoding ? (
        // ── IDE split view: problem statement | editor + console ──
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            border: `1px solid ${PALETTE.border}`,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          {/* LEFT: problem statement pane */}
          <Box sx={{ width: `${splitPct}%`, minWidth: 260, display: "flex", flexDirection: "column", borderRight: `1px solid ${PALETTE.border}`, bgcolor: "#fff" }}>
            {/* ── 3-tab nav: Statement | Submissions | Solution ── */}
            <Box sx={{ borderBottom: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight, flexShrink: 0 }}>

              {/* Problem navigator — only shown when lesson has more than one problem */}
              {problems.length > 1 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 1.5, pt: 1, pb: 0.5, borderBottom: `1px solid ${PALETTE.border}` }}
                >
                  {/* Prev button */}
                  <Button
                    size="small"
                    disabled={activeProblemIndex === 0}
                    onClick={() => setActiveProblemIndex((i: number) => Math.max(0, i - 1))}
                    sx={{ minWidth: 0, px: 0.75, py: 0.25, color: PALETTE.textSecondary, "&:hover": { bgcolor: "#eff6ff" } }}
                  >
                    ‹
                  </Button>

                  {/* Problem dots */}
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {problems.map((p: any, idx: number) => {
                      const key = problemKey(p, idx);
                      const st = problemStates[key];
                      const solved = isSolved(st);
                      const active = idx === activeProblemIndex;
                      return (
                        <Box
                          key={key}
                          onClick={() => setActiveProblemIndex(idx)}
                          title={`${idx + 1}. ${p.title}`}
                          sx={{
                            width: active ? 22 : 8,
                            height: 8,
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "all 0.18s",
                            bgcolor: active ? "#1d4ed8" : solved ? "#16a34a" : "#cbd5e1",
                            "&:hover": { bgcolor: active ? "#1d4ed8" : solved ? "#15803d" : "#94a3b8" },
                          }}
                        />
                      );
                    })}
                  </Stack>

                  <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ flex: 1, minWidth: 0, justifyContent: "center" }}
                >
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#64748b" }}>
                      {activeProblemIndex + 1}/{problems.length}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>
                      {activeProblem?.title ?? ""}
                    </Typography>
                    {isSolved(activeState) && (
                      <CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a", flexShrink: 0 }} />
                    )}
                  </Stack>

                  {/* Next button */}
                  <Button
                    size="small"
                    disabled={activeProblemIndex === problems.length - 1}
                    onClick={() => setActiveProblemIndex((i: number) => Math.min(problems.length - 1, i + 1))}
                    sx={{ minWidth: 0, px: 0.75, py: 0.25, color: PALETTE.textSecondary, "&:hover": { bgcolor: "#eff6ff" } }}
                  >
                    ›
                  </Button>
                </Stack>
              )}

              <ProblemPaneTabs
                activeState={activeState}
                activeProblem={activeProblem}
                solved={isSolved(activeState)}
                onTabChange={(tab) => activeKey && setProblemTab(activeKey, tab)}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", p: 2.25 }}>
              {/* Statement tab content */}
              {activeState?._activeTab !== "submissions" && activeState?._activeTab !== "solution" && (
                <>
                  {activeLesson.content && (
                    <Box
                      sx={{
                        fontSize: "13px",
                        lineHeight: 1.7,
                        color: "#374151",
                        mb: 2.5,
                        "& pre": { bgcolor: "#1e293b", color: "#e2e8f0", p: 1.5, borderRadius: "8px", overflowX: "auto", fontFamily: "monospace", fontSize: "12px" },
                      }}
                      dangerouslySetInnerHTML={{ __html: activeLesson.content.replace(/\n/g, "<br/>") }}
                    />
                  )}

                  {activeProblem && (
                    <Box sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography sx={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>{activeProblem.title}</Typography>
                        <DifficultyChip difficulty={activeProblem.difficulty} />
                      </Stack>
                      <Typography sx={{ fontSize: "13px", color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{activeProblem.description}</Typography>

                      {activeProblem.instructorNote && (
                        <Alert severity="info" sx={{ mt: 1.5, "& .MuiAlert-message": { fontSize: "12px" } }}>
                          <strong>Instructor note:</strong> {activeProblem.instructorNote}
                        </Alert>
                      )}

                      {(activeProblem.tags?.length > 0 || activeProblem.topics?.length > 0) && (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: "wrap", gap: 0.75 }}>
                          {[...(activeProblem.tags ?? []), ...(activeProblem.topics ?? [])].slice(0, 6).map((t: string) => (
                            <Chip key={t} size="small" label={t} sx={{ fontSize: "10px", height: 22 }} />
                          ))}
                        </Stack>
                      )}

                      {activeProblem.constraints && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography sx={{ fontSize: "11px", fontWeight: 800, color: PALETTE.textMuted, textTransform: "uppercase", mb: 0.5 }}>Constraints</Typography>
                          <Typography component="pre" sx={{ fontSize: "12px", fontFamily: "monospace", color: "#374151", whiteSpace: "pre-wrap", m: 0 }}>
                            {activeProblem.constraints}
                          </Typography>
                        </Box>
                      )}

                      {/* Test cases / examples */}
                      {(Array.isArray(activeProblem.examples) && activeProblem.examples.length > 0
                        ? activeProblem.examples.map((ex: any, ei: number) => (
                          <Box key={ei} sx={{ mt: 1.5, bgcolor: "#f8fafc", border: `1px solid ${PALETTE.border}`, borderRadius: "8px", p: 1.5 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 800, color: PALETTE.textMuted, mb: 0.5 }}>Example {ei + 1}</Typography>
                            <Typography component="pre" sx={{ fontSize: "12px", fontFamily: "monospace", m: 0, whiteSpace: "pre-wrap" }}>
                              Input: {ex.input}
                              {"\n"}Output: {ex.output}
                            </Typography>
                            {ex.explanation && (
                              <Typography sx={{ fontSize: "12px", color: PALETTE.textSecondary, mt: 0.5 }}>{ex.explanation}</Typography>
                            )}
                          </Box>
                        ))
                        : (activeProblem.testCases ?? []).map((tc: any, ei: number) => (
                          <Box key={tc.name ?? ei} sx={{ mt: 1.5, bgcolor: "#f8fafc", border: `1px solid ${PALETTE.border}`, borderRadius: "8px", p: 1.5 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 800, color: PALETTE.textMuted, mb: 0.5 }}>{tc.name ?? `Example ${ei + 1}`}</Typography>
                            <Typography component="pre" sx={{ fontSize: "12px", fontFamily: "monospace", m: 0, whiteSpace: "pre-wrap" }}>
                              {`Input: ${tc.input}\nExpected: ${tc.expected}`}
                            </Typography>
                          </Box>
                        ))
                      )}

                      {/* Hints only (editorial moved to Solution tab) */}
                      <ProblemHintsOnly problem={activeProblem} />
                    </Box>
                  )}

                  {(activeLesson.type === "quiz" || activeLesson.type === "mixed") && (activeLesson.quizQuestions ?? []).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "#0f172a", mb: 1.25 }}>Quiz</Typography>
                      {(activeLesson.quizQuestions ?? []).map((q: any, i: number) => (
                        <Box key={i} sx={{ mb: 2 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 0.75 }}>
                            {i + 1}. {q.question}
                          </Typography>
                          <RadioGroup value={quizAnswers[i] ?? ""} onChange={(e) => !quizSubmitted && setQuizAnswers((p: any) => ({ ...p, [i]: e.target.value }))}>
                            {(q.options ?? []).map((opt: string, oi: number) => {
                              const isCorrect = quizSubmitted && opt === q.correctAnswer;
                              const isWrong = quizSubmitted && quizAnswers[i] === opt && opt !== q.correctAnswer;
                              return (
                                <FormControlLabel
                                  key={oi}
                                  value={opt}
                                  control={<Radio size="small" />}
                                  label={
                                    <Typography sx={{ fontSize: "12px", color: isCorrect ? "#16a34a" : isWrong ? "#dc2626" : "#374151", fontWeight: isCorrect || isWrong ? 700 : 400 }}>
                                      {opt}
                                    </Typography>
                                  }
                                />
                              );
                            })}
                          </RadioGroup>
                          {quizSubmitted && q.explanation && (
                            <Alert severity="info" sx={{ mt: 0.75, "& .MuiAlert-message": { fontSize: "12px" } }}>
                              {q.explanation}
                            </Alert>
                          )}
                        </Box>
                      ))}
                      {!quizSubmitted ? (
                        <Button variant="contained" size="small" onClick={submitQuiz} sx={{ textTransform: "none" }}>
                          Submit Quiz
                        </Button>
                      ) : (
                        <Alert severity={quizScore / (activeLesson.quizQuestions?.length ?? 1) >= 0.6 ? "success" : "warning"}>
                          Score: {quizScore}/{activeLesson.quizQuestions?.length ?? 0}{" "}
                          {quizScore / (activeLesson.quizQuestions?.length ?? 1) >= 0.6 ? "✓ Passed" : "— Need 60% to pass"}
                        </Alert>
                      )}
                    </Box>
                  )}
                </>
              )}

              {/* Submissions tab content */}
              {activeState?._activeTab === "submissions" && (
                <SubmissionsTab submissions={activeState?.submissions ?? []} />
              )}

              {/* Solution tab content */}
              {activeState?._activeTab === "solution" && (
                <SolutionTab
                  problem={activeProblem}
                  solved={isSolved(activeState)}
                />
              )}
            </Box>
          </Box>

          {/* DRAG HANDLE */}
          <Box
            onMouseDown={() => {
              dragging.current = true;
              document.body.style.cursor = "col-resize";
            }}
            sx={{ width: "5px", cursor: "col-resize", bgcolor: PALETTE.border, flexShrink: 0, "&:hover": { bgcolor: "#1d4ed8" } }}
          />

          {/* RIGHT: editor + console */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", bgcolor: EDITOR.bg }}>
            {/* editor toolbar */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.5, py: 0.75, bgcolor: EDITOR.bgHeader, borderBottom: `1px solid ${EDITOR.border}`, flexShrink: 0 }}
            >
              <FormControl size="small" sx={{ width: 150 }}>
                {allowedLanguages.length === 1 ? (
                  <Typography sx={{ fontSize: "12px", fontWeight: 700, color: EDITOR.text, px: 0.5 }}>
                    {allowedLanguages[0].name}
                  </Typography>
                ) : (
                  <Select
                    value={activeState?.language?.value ?? allowedLanguages[0]?.value ?? LANGUAGES_LIST[0].value}
                    disabled={!activeProblem}
                    onChange={(e) => {
                      const l = allowedLanguages.find((x) => x.value === e.target.value) ?? allowedLanguages[0];
                      if (activeKey && activeProblem && l) setProblemLanguage(activeKey, activeProblem, l);
                    }}
                    sx={{
                      fontSize: "12px",
                      color: EDITOR.text,
                      bgcolor: "#2d2d2d",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: EDITOR.border },
                      "& .MuiSvgIcon-root": { color: EDITOR.textMuted },
                    }}
                  >
                    {allowedLanguages.map((l) => (
                      <MenuItem key={l.value} value={l.value} sx={{ fontSize: "12px" }}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
                  disabled={!activeProblem || !!runningKey}
                  onClick={() => runCode(activeProblemIndex, false)}
                  sx={{ textTransform: "none", fontSize: "12px", fontWeight: 700, color: EDITOR.text, borderColor: EDITOR.border, "&:hover": { borderColor: EDITOR.accent, bgcolor: "rgba(59,130,246,0.08)" } }}
                >
                  Run
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SendIcon sx={{ fontSize: 16 }} />}
                  disabled={!activeProblem || !!runningKey}
                  onClick={() => runCode(activeProblemIndex, true)}
                  sx={{ textTransform: "none", fontSize: "12px", fontWeight: 700, bgcolor: EDITOR.submit, "&:hover": { bgcolor: EDITOR.submitDark } }}
                >
                  Submit
                </Button>
              </Stack>
            </Stack>

            {/* code area */}
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
              <TextField
                multiline
                fullWidth
                value={activeState?.code ?? ""}
                disabled={!activeProblem}
                onChange={(e) => activeKey && setProblemCode(activeKey, e.target.value)}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  height: "100%",
                  "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start", p: "12px 16px" },
                  "& textarea": {
                    height: "100% !important",
                    overflow: "auto !important",
                    fontFamily: "'Fira Code', 'Consolas', monospace",
                    fontSize: "13px",
                    lineHeight: 1.6,
                    color: EDITOR.text,
                  },
                }}
              />
            </Box>

            {runningKey === activeKey && <LinearProgress sx={{ flexShrink: 0 }} />}

            {/* console */}
            <Box sx={{ height: 190, flexShrink: 0, borderTop: `1px solid ${EDITOR.border}`, bgcolor: EDITOR.bgConsole, display: "flex", flexDirection: "column" }}>
              <Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${EDITOR.border}`, flexShrink: 0 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 800, color: EDITOR.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Console{problems.length > 1 && activeProblem ? ` · ${activeProblem.title}` : ""}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
                {activeState?.execution ? (
                  <ExecutionResultPanel execution={activeState.execution} />
                ) : (
                  <Typography sx={{ fontSize: "12px", color: EDITOR.textMuted }}>Run your code to see the output here.</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        // ── Non-coding lesson: single reading pane, same visual language ──
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <Stack spacing={1.5} sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
            <SectionPaper>
              <Box sx={{ p: 3 }}>
                <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }}>
                  <Chip size="small" label={LESSON_LABEL[activeLesson.type] ?? activeLesson.type} sx={{ fontSize: "11px", height: "20px", textTransform: "capitalize" }} />
                  {activeLesson.xpReward > 0 && (
                    <Chip size="small" label={`+${activeLesson.xpReward} XP`} sx={{ fontSize: "11px", height: "20px", bgcolor: "#f0fdf4", color: "#16a34a" }} />
                  )}
                  {activeLesson.progress?.completed && (
                    <Chip size="small" label="✓ Completed" sx={{ fontSize: "11px", height: "20px", bgcolor: "#f0fdf4", color: "#16a34a" }} />
                  )}
                </Stack>
                <Typography sx={{ fontSize: "22px", fontWeight: 900, color: "#0f172a", mb: 2 }}>{activeLesson.title}</Typography>
                {activeLesson.content && (
                  <Box
                    sx={{
                      fontSize: "14px",
                      lineHeight: 1.8,
                      color: "#374151",
                      "& pre": { bgcolor: "#1e293b", color: "#e2e8f0", p: 1.5, borderRadius: "8px", overflowX: "auto", fontFamily: "monospace", fontSize: "12.5px" },
                    }}
                    dangerouslySetInnerHTML={{ __html: activeLesson.content.replace(/\n/g, "<br/>") }}
                  />
                )}
              </Box>
            </SectionPaper>

            {activeLesson.type === "quiz" && (activeLesson.quizQuestions ?? []).length > 0 && (
              <SectionPaper>
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>Quiz</Typography>
                </Box>
                <Box sx={{ p: 1.75 }}>
                  {(activeLesson.quizQuestions ?? []).map((q: any, i: number) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 0.75 }}>
                        {i + 1}. {q.question}
                      </Typography>
                      <RadioGroup value={quizAnswers[i] ?? ""} onChange={(e) => !quizSubmitted && setQuizAnswers((p: any) => ({ ...p, [i]: e.target.value }))}>
                        {(q.options ?? []).map((opt: string, oi: number) => {
                          const isCorrect = quizSubmitted && opt === q.correctAnswer;
                          const isWrong = quizSubmitted && quizAnswers[i] === opt && opt !== q.correctAnswer;
                          return (
                            <FormControlLabel
                              key={oi}
                              value={opt}
                              control={<Radio size="small" />}
                              label={
                                <Typography sx={{ fontSize: "12px", color: isCorrect ? "#16a34a" : isWrong ? "#dc2626" : "#374151", fontWeight: isCorrect || isWrong ? 700 : 400 }}>
                                  {opt}
                                </Typography>
                              }
                            />
                          );
                        })}
                      </RadioGroup>
                      {quizSubmitted && q.explanation && (
                        <Alert severity="info" sx={{ mt: 0.75, "& .MuiAlert-message": { fontSize: "12px" } }}>
                          {q.explanation}
                        </Alert>
                      )}
                    </Box>
                  ))}
                  {!quizSubmitted ? (
                    <Button variant="contained" size="small" onClick={submitQuiz} sx={{ textTransform: "none" }}>
                      Submit Quiz
                    </Button>
                  ) : (
                    <Alert severity={quizScore / (activeLesson.quizQuestions?.length ?? 1) >= 0.6 ? "success" : "warning"}>
                      Score: {quizScore}/{activeLesson.quizQuestions?.length ?? 0}{" "}
                      {quizScore / (activeLesson.quizQuestions?.length ?? 1) >= 0.6 ? "✓ Passed" : "— Need 60% to pass"}
                    </Alert>
                  )}
                </Box>
              </SectionPaper>
            )}

            <LessonNavButtons
              hasNextProblem={false}
              hasNextLesson={!!nextLesson}
              nextLessonTitle={nextLesson?.title}
              onNextLesson={goNextLesson}
              hasPrevProblem={false}
              hasPrevLesson={!!prevLesson}
              prevLessonTitle={prevLesson?.title}
              onPrevLesson={goPrevLesson}
              trailing={(
                <Button
                  variant="contained"
                  disabled={completing || activeLesson.progress?.completed}
                  onClick={markComplete}
                  sx={{ textTransform: "none" }}
                >
                  {completing ? "Completing..." : activeLesson.progress?.completed ? "✓ Completed" : "Mark as Complete"}
                </Button>
              )}
            />
          </Stack>
        </Box>
      )}

      {isCoding && (
        <Box sx={{ pt: 1.25, flexShrink: 0 }}>
          <LessonNavButtons
            hasNextProblem={hasNextProblem}
            hasNextLesson={!!nextLesson}
            nextProblemTitle={problems[activeProblemIndex + 1]?.title}
            nextLessonTitle={nextLesson?.title}
            onNextProblem={goNextProblem}
            onNextLesson={goNextLesson}
            hasPrevProblem={hasPrevProblem}
            hasPrevLesson={!!prevLesson}
            prevProblemTitle={problems[activeProblemIndex - 1]?.title}
            prevLessonTitle={prevLesson?.title}
            onPrevProblem={goPrevProblem}
            onPrevLesson={goPrevLesson}
            trailing={(
              <Button
                variant="contained"
                disabled={completing || activeLesson.progress?.completed}
                onClick={markComplete}
                sx={{ textTransform: "none" }}
              >
                {completing ? "Completing..." : activeLesson.progress?.completed ? "✓ Completed" : "Mark as Complete"}
              </Button>
            )}
          />
        </Box>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Problem pane — 3-tab header (Statement | Submissions | Solution)
// ════════════════════════════════════════════════════════════════════════════
function ProblemPaneTabs({
  activeState,
  activeProblem,
  solved,
  onTabChange,
}: {
  activeState: any;
  activeProblem: any;
  solved: boolean;
  onTabChange: (tab: string) => void;
}) {
  const activeTab = activeState?._activeTab ?? "statement";
  const submissionCount = (activeState?.submissions ?? []).length;

  if (!activeProblem) return null;

  return (
    <Tabs
      value={activeTab}
      onChange={(_e, v) => onTabChange(v)}
      sx={{
        minHeight: 38,
        px: 1,
        "& .MuiTab-root": { minHeight: 38, textTransform: "none", fontSize: "12px", fontWeight: 700, px: 1.5, py: 0 },
        "& .MuiTabs-indicator": { height: 2, borderRadius: "2px 2px 0 0" },
      }}
    >
      <Tab
        value="statement"
        label={
          <Stack direction="row" spacing={0.6} alignItems="center">
            <SubjectOutlinedIcon sx={{ fontSize: 14 }} />
            <span>Statement</span>
          </Stack>
        }
      />
      <Tab
        value="submissions"
        label={
          <Stack direction="row" spacing={0.6} alignItems="center">
            <HistoryOutlinedIcon sx={{ fontSize: 14 }} />
            <span>Submissions</span>
            {submissionCount > 0 && (
              <Chip
                size="small"
                label={submissionCount}
                sx={{ height: 16, fontSize: "10px", fontWeight: 800, ml: 0.25, bgcolor: solved ? "#dcfce7" : "#eff6ff", color: solved ? "#16a34a" : "#1d4ed8", "& .MuiChip-label": { px: 0.75 } }}
              />
            )}
          </Stack>
        }
      />
      <Tab
        value="solution"
        label={
          <Stack direction="row" spacing={0.6} alignItems="center">
            <MenuBookOutlinedIcon sx={{ fontSize: 14 }} />
            <span>Solution</span>
            {!solved && <LockOutlinedIcon sx={{ fontSize: 12, opacity: 0.55 }} />}
          </Stack>
        }
      />
    </Tabs>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Submissions tab — list of submitted code with results
// ════════════════════════════════════════════════════════════════════════════
function SubmissionsTab({ submissions }: { submissions: Array<{ code: string; language: string; result: any; submittedAt: Date }> }) {
  const [expanded, setExpanded] = useState<number | null>(0);

  if (submissions.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: "center" }}>
        <HistoryOutlinedIcon sx={{ fontSize: 36, color: PALETTE.textMuted, mb: 1 }} />
        <Typography sx={{ fontSize: "13px", color: PALETTE.textMuted }}>No submissions yet.</Typography>
        <Typography sx={{ fontSize: "12px", color: PALETTE.textMuted, mt: 0.5 }}>Submit your code to see results here.</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: PALETTE.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
      </Typography>
      {submissions.map((sub, i) => {
        const status = sub.result?.status ?? "unknown";
        const isAccepted = status === "accepted";
        const isOpen = expanded === i;
        const statusColor = isAccepted ? "#16a34a" : "#dc2626";
        const statusBg = isAccepted ? "#dcfce7" : "#fee2e2";
        const passed = sub.result?.passedTestCases ?? 0;
        const total = sub.result?.totalTestCases ?? 0;

        return (
          <Paper
            key={i}
            variant="outlined"
            sx={{ borderRadius: "8px", overflow: "hidden", borderColor: isAccepted ? "#86efac" : PALETTE.border }}
          >
            {/* Row header */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              onClick={() => setExpanded(isOpen ? null : i)}
              sx={{ px: 1.5, py: 1, cursor: "pointer", bgcolor: isOpen ? "#f8fafc" : "#fff", "&:hover": { bgcolor: "#f8fafc" } }}
            >
              <Chip
                size="small"
                label={isAccepted ? "Accepted" : status.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}
                sx={{ bgcolor: statusBg, color: statusColor, fontWeight: 700, fontSize: "11px", height: 20, "& .MuiChip-label": { px: 0.9 } }}
              />
              {total > 0 && (
                <Typography sx={{ fontSize: "11px", color: PALETTE.textSecondary }}>
                  {passed}/{total} tests
                </Typography>
              )}
              <Chip
                size="small"
                label={sub.language}
                sx={{ height: 18, fontSize: "10px", bgcolor: "#eff6ff", color: "#1d4ed8", "& .MuiChip-label": { px: 0.75 } }}
              />
              <Typography sx={{ fontSize: "10px", color: PALETTE.textMuted, ml: "auto !important" }}>
                {sub.submittedAt instanceof Date
                  ? sub.submittedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
                  : ""}
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 16, color: PALETTE.textMuted, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }} />
            </Stack>

            {/* Expanded: code + error output */}
            <Collapse in={isOpen}>
              <Divider />
              <Box sx={{ bgcolor: "#1e1e1e", p: 1.5 }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>Submitted Code</Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontFamily: "'Fira Code','Consolas',monospace",
                    fontSize: "12px",
                    color: "#d4d4d4",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    lineHeight: 1.6,
                    maxHeight: 280,
                    overflowY: "auto",
                  }}
                >
                  {sub.code}
                </Box>
              </Box>
              {sub.result?.error && (
                <Box sx={{ bgcolor: "#fff5f5", p: 1.25, borderTop: "1px solid #fecaca" }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#dc2626", mb: 0.4 }}>Error</Typography>
                  <Typography component="pre" sx={{ fontSize: "11px", fontFamily: "monospace", color: "#b91c1c", m: 0, whiteSpace: "pre-wrap" }}>
                    {sub.result.error}
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Solution tab — editorial with syntax-highlighted code block
// ════════════════════════════════════════════════════════════════════════════
function SolutionTab({ problem, solved }: { problem: any; solved: boolean }) {
  const hasEditorial = !!problem?.editorial;
  const locked = !solved;

  if (!problem) {
    return (
      <Box sx={{ py: 5, textAlign: "center" }}>
        <Typography sx={{ fontSize: "13px", color: PALETTE.textMuted }}>No problem selected.</Typography>
      </Box>
    );
  }

  if (locked) {
    return (
      <Box sx={{ py: 5, textAlign: "center" }}>
        <LockOutlinedIcon sx={{ fontSize: 38, color: PALETTE.textMuted, mb: 1.5 }} />
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", mb: 0.5 }}>Solution Locked</Typography>
        <Typography sx={{ fontSize: "12.5px", color: PALETTE.textSecondary, maxWidth: 280, mx: "auto" }}>
          Solve this problem first to unlock the solution.
        </Typography>
      </Box>
    );
  }

  if (!hasEditorial) {
    return (
      <Box sx={{ py: 5, textAlign: "center" }}>
        <MenuBookOutlinedIcon sx={{ fontSize: 36, color: PALETTE.textMuted, mb: 1 }} />
        <Typography sx={{ fontSize: "13px", color: PALETTE.textMuted }}>No solution available for this problem.</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
          <MenuBookOutlinedIcon sx={{ fontSize: 16, color: "#1d4ed8" }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.3 }}>Solution</Typography>
          <Chip size="small" label="Accepted" sx={{ height: 18, fontSize: "10px", fontWeight: 700, bgcolor: "#dcfce7", color: "#16a34a", "& .MuiChip-label": { px: 0.75 } }} />
        </Stack>
        {problem.programmingLanguages?.length > 0 && (
          <Chip
            size="small"
            label={problem.programmingLanguages[0]}
            sx={{ mb: 1.25, height: 20, fontSize: "11px", bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 600, textTransform: "capitalize" }}
          />
        )}
        <Box sx={{ bgcolor: "#1e1e1e", borderRadius: "8px", overflow: "hidden" }}>
          <Box sx={{ px: 1.5, py: 0.75, bgcolor: "#252526", borderBottom: "1px solid #333" }}>
            <Typography sx={{ fontSize: "10px", color: "#8a8a8a", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {problem.programmingLanguages?.[0] ?? "Code"}
            </Typography>
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.75,
              fontFamily: "'Fira Code','Consolas',monospace",
              fontSize: "12.5px",
              color: "#d4d4d4",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              lineHeight: 1.65,
              maxHeight: 480,
              overflowY: "auto",
            }}
          >
            {problem.editorial}
          </Box>
        </Box>
      </Box>

      {/* Hints still accessible in Solution tab */}
      <ProblemHintsOnly problem={problem} />
    </Stack>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Hints-only sub-component (no editorial — that's moved to Solution tab)
// ════════════════════════════════════════════════════════════════════════════
function ProblemHintsOnly({ problem }: { problem: any }) {
  const [revealedHints, setRevealedHints] = useState(0);
  const hints: string[] = problem?.showHints !== false ? (problem?.hints ?? []).filter(Boolean) : [];

  useEffect(() => {
    setRevealedHints(0);
  }, [problem?.id, problem?._id]);

  if (!hints.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <LightbulbOutlinedIcon sx={{ fontSize: 16, color: "#d97706" }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.3 }}>
          Hints
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {hints.slice(0, revealedHints).map((hint, i) => (
          <Alert key={i} severity="warning" sx={{ "& .MuiAlert-message": { fontSize: "12px", lineHeight: 1.55 } }}>
            <strong>Hint {i + 1}:</strong> {hint}
          </Alert>
        ))}
        {revealedHints < hints.length && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setRevealedHints((n) => Math.min(n + 1, hints.length))}
            sx={{ textTransform: "none", alignSelf: "flex-start", fontSize: 12, fontWeight: 700 }}
          >
            {revealedHints === 0 ? "Show Hint" : `Show Hint ${revealedHints + 1}`}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function LessonNavButtons({
  hasNextProblem,
  hasNextLesson,
  nextProblemTitle,
  nextLessonTitle,
  onNextProblem,
  onNextLesson,
  hasPrevProblem,
  hasPrevLesson,
  prevProblemTitle,
  prevLessonTitle,
  onPrevProblem,
  onPrevLesson,
  trailing,
}: {
  hasNextProblem: boolean;
  hasNextLesson: boolean;
  nextProblemTitle?: string;
  nextLessonTitle?: string;
  onNextProblem?: () => void;
  onNextLesson?: () => void;
  hasPrevProblem?: boolean;
  hasPrevLesson?: boolean;
  prevProblemTitle?: string;
  prevLessonTitle?: string;
  onPrevProblem?: () => void;
  onPrevLesson?: () => void;
  trailing?: React.ReactNode;
}) {
  const hasAny = hasNextProblem || hasNextLesson || hasPrevProblem || hasPrevLesson || !!trailing;
  if (!hasAny) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ flexWrap: "wrap", gap: 1 }}>
      {/* Left: back navigation */}
      <Stack direction="row" spacing={1}>
        {hasPrevProblem && onPrevProblem && (
          <Button
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            onClick={onPrevProblem}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}
          >
            Prev Problem
          </Button>
        )}
        {hasPrevLesson && onPrevLesson && (
          <Button
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            onClick={onPrevLesson}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}
          >
            Prev Lesson
          </Button>
        )}
      </Stack>

      {/* Right: forward navigation + trailing actions */}
      <Stack direction="row" spacing={1} alignItems="center">
        {hasNextProblem && onNextProblem && (
          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            onClick={onNextProblem}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}
          >
            Next Problem
          </Button>
        )}
        {hasNextLesson && onNextLesson && (
          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            onClick={onNextLesson}
            sx={{ textTransform: "none", fontSize: 12, fontWeight: 700 }}
          >
            Next Lesson
          </Button>
        )}
        {trailing}
      </Stack>
    </Stack>
  );
}